import { atom, type createStore, type Getter, type Setter } from "jotai";
import { atomWithStorage, RESET } from "jotai/utils";
import { omit } from "remeda";
import { readActiveLocationId } from "services/atoms";
import { captureException } from "utils/errorReporting";
import { safeJSONStorage, safeLocalStorage, safeSessionStorage } from "utils/safeStorage";
import type { RecentAction } from "./page-context";

// No cross-tab sync: a tab must keep sending the conversation its transcript shows. The last write wins on reload.
const pointerStorage = omit(safeJSONStorage<Record<string, string>>(), ["subscribe"]);

const POINTERS_KEY = "qwick-ai-chat-conversations";
// Before one chat per location the pointer was a single global conversation id.
const LEGACY_KEY = "qwick-ai-chat-conversation";

/** The server conversation each location's chat continues, by location id. */
const conversationPointersAtom = atomWithStorage<Record<string, string>>(POINTERS_KEY, {}, pointerStorage, {
  getOnInit: true,
});

/** The location the mounted panel is chatting at; set by the panel, since the app's location is not reactive to atoms. */
export const chatLocationAtom = atom<number | undefined>(undefined);

const slotFor = (locationId: number | undefined) => String(locationId ?? "none");

const ambientSlot = (get: Getter) => slotFor(get(chatLocationAtom) ?? readActiveLocationId(get));

// Every write is stored at once, so what is stored is the newest map. This tab's in-memory copy may be stale for
// the other locations: another tab may have started or cleared their chats since it was read.
const writePointer = (set: Setter, slot: string, next: string | null) => {
  const { [slot]: _dropped, ...others } = pointerStorage.getItem(POINTERS_KEY, {});
  set(conversationPointersAtom, next === null ? others : { ...others, [slot]: next });
};

/**
 * The server conversation the panel continues at the chat's location; null starts a new one on the next send.
 * A write lands in the slot of the location the app is at when it runs: use `setConversationPointerAtom` for work
 * that can outlive a location switch.
 */
export const conversationIdAtom = atom(
  (get) => get(conversationPointersAtom)[ambientSlot(get)] ?? null,
  (get, set, next: string | null | typeof RESET) => {
    // RESET is the atom's reset to its initial value, as before the pointers were per location: no chat anywhere.
    if (next === RESET) set(conversationPointersAtom, {});
    else writePointer(set, ambientSlot(get), next);
  },
);

/** Writes one location's pointer, for a lookup, a finished turn or a hand-back that may land after a location switch. */
export const setConversationPointerAtom = atom(
  null,
  (_get, set, { locationId, conversationId }: { locationId: number | undefined; conversationId: string | null }) =>
    writePointer(set, slotFor(locationId), conversationId),
);

/** The location a chat started now belongs to: the panel's, else the app's. */
export const chatLocationOf = (store: Pick<ReturnType<typeof createStore>, "get">) =>
  store.get(chatLocationAtom) ?? readActiveLocationId(store.get);

/** Moves the old single pointer into the location's slot, once: the chat it continues was at the location the app is on. */
export const migrateLegacyPointerAtom = atom(null, (_get, set, locationId: number | undefined) => {
  const legacy = safeLocalStorage.getItem(LEGACY_KEY);
  if (legacy === null) return;
  safeLocalStorage.removeItem(LEGACY_KEY);
  const slot = slotFor(locationId);
  const stored = pointerStorage.getItem(POINTERS_KEY, {});
  try {
    const id: unknown = JSON.parse(legacy);
    if (typeof id === "string" && !(slot in stored)) set(conversationPointersAtom, { ...stored, [slot]: id });
  } catch (error) {
    // An unreadable pointer is the same as none, but a migration that failed should be seen.
    captureException(error, { tags: { area: "ai-chat" } });
  }
});

/**
 * The posting page's own conversation, by `"<user id>:<location id>"` like its draft: kept when the manager leaves the page
 * and when the draft is reset, so coming back carries the same chat on. In this tab only (sessionStorage, no cross-tab
 * sync); New chat and logout clear it. The dock's conversation never goes here.
 */
export const pageConversationsAtom = atomWithStorage<Record<string, string>>(
  "qwick-post-page-conversations",
  {},
  omit(safeJSONStorage<Record<string, string>>(safeSessionStorage), ["subscribe"]),
  { getOnInit: true },
);

/** Forgets the posting page's conversation for one manager and location (New chat, or one the server no longer has). */
export const forgetPageConversation = (store: Pick<ReturnType<typeof createStore>, "set">, pageKey: string) =>
  store.set(pageConversationsAtom, (all) => omit(all, [pageKey]));

const RECENT_ACTION_CAPACITY = 5;

export const recentActionsAtom = atom<Array<RecentAction>>([]);

export const recordRecentActionAtom = atom(null, (_get, set, action: Omit<RecentAction, "at">) =>
  set(recentActionsAtom, (prev) =>
    [...prev, { ...action, at: new Date().toISOString() }].slice(-RECENT_ACTION_CAPACITY),
  ),
);

/** `gig:pro` pairs offered this session; the manager API does not list pending offers, so this is the card's memory. */
export const sentOffersAtom = atom<ReadonlySet<string>>(new Set<string>());

/** `gig:pro` pairs whose send got no clear answer, so the offer may exist; never offered again. */
export const unconfirmedOffersAtom = atom<ReadonlySet<string>>(new Set<string>());

export const sentOfferKey = (gigId: string, proId: string) => `${gigId}:${proId}`;
